/*
  Consultation form (rebuilt /pages/contact) — v1.

  Same pattern as build-your-system.js: one custom element, a step state
  machine, tiles for single/multi-select answers, everything driven from a
  JSON data blob (services + their field lists) instead of one hand-authored
  template per service — so adding/editing questions later is a data change,
  not a markup change.

  Lead delivery reuses Shopify's own native contact form action (safe,
  already works — no server-side piece needed, since a real Admin API write
  needs a token that can't live in theme JS). Two submissions happen at most:
  one right after Contact Details (every answer, clearly labeled), and a
  second if the visitor schedules a call (adds the chosen day/time/method).

  Submission goes through a real, hidden {% form 'contact' %} (see
  contact-form.liquid) via requestSubmit(), not fetch(). Shopify's /contact
  endpoint now requires a genuine browser form-submit to carry its anti-spam
  token; fetch() bypassed that and every lead was silently dropped (confirmed
  via a real test submission returning 400 "Missing CAPTCHA token").
  requestSubmit() dispatches a real submit event (unlike .submit()), so any
  Shopify-side listener that injects that token still fires.

  No target/popup/iframe is set on the submit on purpose: confirmed live that
  Shopify's own theme script intercepts this form's submit event itself
  (calls preventDefault before it ever navigates anywhere) and handles
  delivery internally — so the browser never actually navigates the page, no
  popup or hidden frame is needed, and the wizard just stays put. An earlier
  version of this fix opened a small popup window to be a safe target in case
  navigation DID happen; that turned out to be unnecessary and only showed
  visitors a confusing blank tab, so it was removed.

  v1 has no live calendar — "Schedule a Call" shows a fixed weekly slot list,
  not real-time availability. Swap in a real booking app later without
  reworking the rest of the form (see the plan doc for why).
*/

if (!customElements.get('consultation-form')) {
  customElements.define('consultation-form', class ConsultationForm extends HTMLElement {
    constructor() {
      super();
      const raw = this.querySelector('[data-cf-json]');
      this.data = raw ? JSON.parse(raw.textContent) : { services: {} };
      this.whatsappNumber = this.dataset.whatsappNumber;
      this.leadForm = this.querySelector('#CfLeadForm');
      this.leadSubmitBtn = this.querySelector('[data-cf-lead-submit]');

      this.state = {
        service: null,
        answers: {}, // { [fieldId]: string | string[] }
        contact: { name: '', phone: '', email: '', city: '', notes: '' },
        schedule: { day: null, slot: null, method: null },
        leadSubmitted: false
      };
      this.history = [];
      this.current = 'welcome';

      this.bindWelcome();
      this.bindServiceTiles();
      this.bindBack();
      this.bindQuestionsNext();
      this.bindContactNext();
      this.bindNextStep();
      this.bindScheduleModal();

      this.applyPreselectedService();
    }

    // ---------- navigation ----------

    goTo(step, opts) {
      opts = opts || {};
      if (!opts.isBack) this.history.push(this.current);
      this.current = step;
      this.querySelectorAll('[data-cf-step]').forEach((el) => {
        el.classList.toggle('is-active', el.dataset.cfStep === step);
      });
      this.updateChrome(step);
      this.updateProgress(step);
      window.scrollTo({ top: this.offsetTop - 20, behavior: 'smooth' });
    }

    // The header/stepper only makes sense once the visitor is inside the
    // 4-step flow — hidden on the standalone Welcome hero and Confirmation
    // screens, matching the reference (neither carries the step chrome).
    updateChrome(step) {
      const chrome = this.querySelector('[data-cf-chrome]');
      if (chrome) chrome.hidden = step === 'welcome' || step === 'confirmation';
    }

    updateProgress(step) {
      const order = ['service', 'questions', 'contact', 'next'];
      const activeIndex = step === 'confirmation' ? order.length : order.indexOf(step);
      this.querySelectorAll('[data-cf-progress] [data-stage]').forEach((el) => {
        const idx = order.indexOf(el.dataset.stage);
        el.classList.toggle('is-done', idx < activeIndex);
        el.classList.toggle('is-active', idx === activeIndex);
      });
    }

    bindBack() {
      this.querySelectorAll('[data-cf-back]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const prev = this.history.pop();
          if (prev) this.goTo(prev, { isBack: true });
        });
      });
    }

    // BYOS's Whole-Home Audio / Commercial / Custom Built dead-ends link here
    // with ?service=whole-home|commercial|home-cinema — arrive already on
    // Step 2, pre-selected, instead of picking a service again.
    applyPreselectedService() {
      const params = new URLSearchParams(window.location.search);
      const slug = params.get('service');
      const map = { 'whole-home': 'wholehome', commercial: 'commercial', 'home-cinema': 'homecinema', hifi: 'hifi' };
      const key = map[slug];
      if (!key || !this.data.services[key]) return;
      this.selectService(key, { skipHistory: true });
    }

    // ---------- step 0: welcome ----------

    bindWelcome() {
      const cta = this.querySelector('[data-welcome-cta]');
      if (cta) cta.addEventListener('click', () => this.goTo('service'));
      this.querySelectorAll('[data-welcome-service]').forEach((btn) => {
        btn.addEventListener('click', () => this.selectService(btn.dataset.welcomeService));
      });
    }

    // ---------- step 1: service ----------

    bindServiceTiles() {
      this.querySelectorAll('[data-service-tiles] [data-service]').forEach((tile) => {
        tile.addEventListener('click', () => this.selectService(tile.dataset.service));
      });
    }

    selectService(key, opts) {
      opts = opts || {};
      this.state.service = key;
      this.state.answers = {};
      this.renderQuestions();
      if (opts.skipHistory) {
        this.current = 'questions';
        this.querySelectorAll('[data-cf-step]').forEach((el) => {
          el.classList.toggle('is-active', el.dataset.cfStep === 'questions');
        });
        this.updateChrome('questions');
        this.updateProgress('questions');
      } else {
        this.goTo('questions');
      }
    }

    // ---------- step 2: service-specific questions ----------

    renderQuestions() {
      const svc = this.data.services[this.state.service];
      if (!svc) return;
      this.querySelector('[data-questions-title]').textContent = svc.title || ('Tell us about your ' + svc.label);
      const wrap = this.querySelector('[data-questions-fields]');
      wrap.innerHTML = '';

      (svc.fields || []).forEach((field) => {
        const fieldEl = document.createElement('div');
        fieldEl.className = 'cf-field';

        const label = document.createElement('h2');
        label.className = 'byos-section-label';
        label.textContent = field.label;
        fieldEl.appendChild(label);

        if (field.type === 'multi') {
          const hint = document.createElement('span');
          hint.className = 'cf-field__hint';
          hint.textContent = '(Select all that apply)';
          label.appendChild(hint);
        }

        if (field.type === 'text') {
          const input = document.createElement('input');
          input.type = 'text';
          input.className = 'cf-input';
          input.placeholder = field.placeholder || '';
          input.value = this.state.answers[field.id] || '';
          input.addEventListener('input', () => { this.state.answers[field.id] = input.value; });
          fieldEl.appendChild(input);
        } else if (field.type === 'multi') {
          const group = document.createElement('div');
          group.className = 'cf-checkbox-grid';
          field.options.forEach(([value, optLabel]) => {
            const current = this.state.answers[field.id] || [];
            const tile = document.createElement('button');
            tile.type = 'button';
            tile.className = 'cf-checkbox';
            tile.classList.toggle('is-selected', current.includes(value));
            tile.innerHTML = '<span class="cf-checkbox__box"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2"><path d="M3.5 8.5l3 3 6-7"/></svg></span><span class="cf-checkbox__label"></span>';
            tile.querySelector('.cf-checkbox__label').textContent = optLabel;
            tile.addEventListener('click', () => {
              const list = this.state.answers[field.id] || [];
              const idx = list.indexOf(value);
              if (idx > -1) list.splice(idx, 1); else list.push(value);
              this.state.answers[field.id] = list;
              tile.classList.toggle('is-selected', list.includes(value));
            });
            group.appendChild(tile);
          });
          fieldEl.appendChild(group);
        } else {
          const group = document.createElement('div');
          group.className = 'cf-option-grid';
          field.options.forEach(([value, optLabel]) => {
            const tile = document.createElement('button');
            tile.type = 'button';
            tile.className = 'cf-option';
            tile.dataset.value = value;
            tile.innerHTML = '<span class="cf-option__label"></span><span class="cf-option__check" aria-hidden="true"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2"><path d="M3.5 8.5l3 3 6-7"/></svg></span>';
            tile.querySelector('.cf-option__label').textContent = optLabel;
            tile.classList.toggle('is-selected', this.state.answers[field.id] === value);
            tile.addEventListener('click', () => {
              this.state.answers[field.id] = value;
              group.querySelectorAll('.cf-option').forEach((t) => t.classList.remove('is-selected'));
              tile.classList.add('is-selected');
            });
            group.appendChild(tile);
          });
          fieldEl.appendChild(group);
        }
        wrap.appendChild(fieldEl);
      });

      const notesField = document.createElement('div');
      notesField.className = 'cf-field';
      const notesLabel = document.createElement('h2');
      notesLabel.className = 'byos-section-label';
      notesLabel.textContent = 'Anything else? (optional)';
      notesField.appendChild(notesLabel);
      const notesInput = document.createElement('textarea');
      notesInput.className = 'cf-textarea';
      notesInput.value = this.state.answers.notes || '';
      notesInput.addEventListener('input', () => { this.state.answers.notes = notesInput.value; });
      notesField.appendChild(notesInput);
      wrap.appendChild(notesField);
    }

    bindQuestionsNext() {
      this.querySelector('[data-questions-next]').addEventListener('click', () => this.goTo('contact'));
    }

    // Turns the raw answers object into clearly labeled lines for the lead
    // email — same field labels the visitor actually saw, not internal ids.
    answersSummary() {
      const svc = this.data.services[this.state.service];
      if (!svc) return [];
      const lines = [];
      (svc.fields || []).forEach((field) => {
        const answer = this.state.answers[field.id];
        if (!answer || (Array.isArray(answer) && !answer.length)) return;
        let text;
        if (field.type === 'text') {
          text = answer;
        } else {
          const optMap = {};
          field.options.forEach(([value, optLabel]) => { optMap[value] = optLabel; });
          text = Array.isArray(answer) ? answer.map((v) => optMap[v] || v).join(', ') : (optMap[answer] || answer);
        }
        lines.push(field.label + ' ' + text);
      });
      if (this.state.answers.notes) lines.push('Anything else: ' + this.state.answers.notes);
      return lines;
    }

    // ---------- step 3: contact details ----------

    bindContactNext() {
      this.querySelectorAll('[data-cf-field]').forEach((input) => {
        input.addEventListener('input', () => {
          this.state.contact[input.dataset.cfField] = input.value;
        });
      });
      this.querySelector('[data-contact-next]').addEventListener('click', () => {
        if (!this.validateContact()) return;
        this.submitLead();
        this.goTo('next');
      });
    }

    validateContact() {
      const c = this.state.contact;
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email || '');
      const ok = c.name && c.phone && c.city && emailOk;
      this.querySelector('[data-contact-error]').hidden = !!ok;
      const requiredFields = ['name', 'phone', 'email', 'city'];
      this.querySelectorAll('[data-cf-field]').forEach((input) => {
        const field = input.dataset.cfField;
        if (!requiredFields.includes(field)) return;
        const invalid = field === 'email' ? !emailOk : !c[field];
        input.classList.toggle('is-invalid', invalid);
      });
      return ok;
    }

    // First submission: everything from Steps 1-3, so the full structured
    // enquiry reaches your inbox immediately, whether or not the visitor
    // goes on to WhatsApp or schedules a call.
    submitLead() {
      if (this.state.leadSubmitted) return;
      this.state.leadSubmitted = true;
      const svc = this.data.services[this.state.service];
      const body = [
        'Service: ' + (svc ? svc.label : this.state.service),
        ...this.answersSummary()
      ].join('\n');
      this.postToShopify(body);
    }

    postToShopify(bodyMessage, extraFields) {
      if (!this.leadForm) return;
      const c = this.state.contact;
      const svc = this.data.services[this.state.service];
      const setHidden = (name, value) => {
        const input = this.leadForm.querySelector('[data-cf-hidden="' + name + '"]');
        if (input) input.value = value || '';
      };
      setHidden('name', c.name);
      setHidden('email', c.email);
      setHidden('phone', c.phone);
      setHidden('City', c.city);
      setHidden('Service', svc ? svc.label : (this.state.service || ''));
      setHidden('body', bodyMessage);
      setHidden('Scheduled Day', '');
      setHidden('Scheduled Time', '');
      setHidden('Contact Method', '');
      (extraFields || []).forEach(([key, value]) => setHidden(key, value));

      if (this.leadForm.requestSubmit) {
        this.leadForm.requestSubmit(this.leadSubmitBtn || undefined);
      } else {
        this.leadForm.submit();
      }
    }

    // ---------- step 4: choose next step ----------

    bindNextStep() {
      this.querySelector('[data-start-whatsapp]').addEventListener('click', () => this.openWhatsapp());
    }

    // Pre-fills the WhatsApp message with everything the visitor already
    // answered (service + project details + contact info), so our team
    // isn't starting the chat from zero — matches the flow doc's "pre-filled
    // WhatsApp message" step. Also lands on Confirmation, same as Schedule a
    // Call, since choosing WhatsApp is just as final a "next step" choice.
    openWhatsapp() {
      if (!this.whatsappNumber) return;
      const svc = this.data.services[this.state.service];
      const c = this.state.contact;
      const lines = [
        "Hi Cinematics, I'm " + (c.name || 'a visitor') + ".",
        "I've submitted an enquiry for: " + (svc ? svc.label : 'a system'),
        ...this.answersSummary(),
        '',
        'Contact: ' + [c.phone, c.email].filter(Boolean).join(' / '),
        'City: ' + (c.city || '')
      ];
      const message = lines.filter((line) => line !== undefined && line !== null && line !== '').join('\n');
      window.open('https://wa.me/' + this.whatsappNumber + '?text=' + encodeURIComponent(message), '_blank');

      const sub = this.querySelector('[data-confirm-sub]');
      if (sub) sub.textContent = "We've opened WhatsApp with your details pre-filled — just hit send and our team will pick up the conversation from there.";
      const details = this.querySelector('[data-confirm-details]');
      if (details) details.hidden = true;
      this.goTo('confirmation');
    }

    // ---------- schedule modal ----------

    bindScheduleModal() {
      const modal = this.querySelector('[data-schedule-modal]');
      const dayList = this.querySelector('[data-day-list]');
      const slotGrid = this.querySelector('[data-slot-grid]');
      const slots = ['11:00 AM', '12:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM'];
      const dowNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

      this.querySelector('[data-open-schedule]').addEventListener('click', () => {
        // Fixed weekly slot list, not a live calendar — next 6 business days
        // from today, same set of times every day. No real-time availability
        // sync yet (see the plan doc's infra notes).
        dayList.innerHTML = '';
        slotGrid.innerHTML = '';
        this.state.schedule = { day: null, slot: null, method: null };
        let added = 0;
        let offset = 1;
        while (added < 6) {
          const d = new Date();
          d.setDate(d.getDate() + offset);
          offset += 1;
          if (d.getDay() === 0) continue; // skip Sunday
          const dayBtn = document.createElement('button');
          dayBtn.type = 'button';
          dayBtn.className = 'cf-day';
          const label = monthNames[d.getMonth()] + ' ' + d.getDate();
          dayBtn.innerHTML = '<span class="cf-day__dow">' + dowNames[d.getDay()] + '</span>' + label;
          dayBtn.dataset.full = dowNames[d.getDay()] + 'day, ' + monthNames[d.getMonth()] + ' ' + d.getDate();
          dayBtn.addEventListener('click', () => {
            dayList.querySelectorAll('.cf-day').forEach((el) => el.classList.remove('is-selected'));
            dayBtn.classList.add('is-selected');
            this.state.schedule.day = dayBtn.dataset.full;
          });
          dayList.appendChild(dayBtn);
          added += 1;
        }
        slots.forEach((slot) => {
          const slotBtn = document.createElement('button');
          slotBtn.type = 'button';
          slotBtn.className = 'cf-slot';
          slotBtn.textContent = slot;
          slotBtn.addEventListener('click', () => {
            slotGrid.querySelectorAll('.cf-slot').forEach((el) => el.classList.remove('is-selected'));
            slotBtn.classList.add('is-selected');
            this.state.schedule.slot = slot;
          });
          slotGrid.appendChild(slotBtn);
        });
        modal.hidden = false;
      });

      this.querySelector('[data-close-schedule]').addEventListener('click', () => { modal.hidden = true; });
      modal.addEventListener('click', (e) => { if (e.target === modal) modal.hidden = true; });

      this.querySelectorAll('[data-method-row] .cf-radio').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.querySelectorAll('[data-method-row] .cf-radio').forEach((el) => el.classList.remove('is-selected'));
          btn.classList.add('is-selected');
          this.state.schedule.method = btn.dataset.method;
        });
      });

      this.querySelector('[data-confirm-booking]').addEventListener('click', () => {
        const s = this.state.schedule;
        if (!s.day || !s.slot || !s.method) return;
        this.postToShopify(
          'Scheduled call requested: ' + s.day + ' at ' + s.slot + ' (' + s.method + ')',
          [['Scheduled Day', s.day], ['Scheduled Time', s.slot], ['Contact Method', s.method]]
        );
        modal.hidden = true;
        this.querySelector('[data-confirm-when]').textContent = s.day + ' · ' + s.slot;
        this.querySelector('[data-confirm-method]').textContent = s.method;
        this.querySelector('[data-confirm-details]').hidden = false;
        this.goTo('confirmation');
      });
    }
  });
}
